import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { GalleryUploadZone } from './GalleryUploadZone';
import { GalleryRowCard, type GalleryRowData } from './GalleryRowCard';
import { ConfirmDialog } from './ConfirmDialog';
import { Lightning } from '@phosphor-icons/react';
import { optimizeImages, formatOptimizationStats, type OptimizationResult } from '@/lib/imageOptimizer';

interface PageImage {
  id: string;
  image_url: string;
  pair_image_url: string | null;
  caption: string | null;
  sort_order: number;
  layout_type: string;
  approve_count?: number;
  reject_count?: number;
}

interface Props {
  pageId: string;
  pageSlug: string;
}

const monoFont = "'IBM Plex Mono', monospace";

export function AdminClientPageImages({ pageId, pageSlug }: Props) {
  const [images, setImages] = useState<PageImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [optStats, setOptStats] = useState<OptimizationResult[]>([]);
  const [dragRowIndex, setDragRowIndex] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null);

  const resolveSignedUrl = async (url: string | null): Promise<string | null> => {
    if (!url) return null;
    const match = url.match(/client-approval-images\/(.+)$/);
    if (match) {
      const { data } = await supabase.storage.from('client-approval-images').createSignedUrl(match[1], 3600);
      return data?.signedUrl || url;
    }
    return url;
  };

  const fetchImages = useCallback(async () => {
    const { data: imgs } = await supabase.from('client_page_images').select('*').eq('page_id', pageId).order('sort_order');
    if (!imgs) { setImages([]); setLoading(false); return; }

    const imageIds = imgs.map((i) => i.id);
    const { data: votes } = imageIds.length
      ? await supabase.from('client_page_votes').select('image_id, approved').in('image_id', imageIds)
      : { data: [] };

    const voteCounts: Record<string, { approve: number; reject: number }> = {};
    (votes || []).forEach((v) => {
      if (!voteCounts[v.image_id]) voteCounts[v.image_id] = { approve: 0, reject: 0 };
      if (v.approved) voteCounts[v.image_id].approve++;
      else voteCounts[v.image_id].reject++;
    });

    const resolvedImages = await Promise.all(
      imgs.map(async (img) => ({
        ...img,
        image_url: (await resolveSignedUrl(img.image_url)) || img.image_url,
        pair_image_url: await resolveSignedUrl(img.pair_image_url),
        approve_count: voteCounts[img.id]?.approve || 0,
        reject_count: voteCounts[img.id]?.reject || 0,
      })),
    );

    setImages(resolvedImages);
    setLoading(false);
  }, [pageId]);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  const uploadToStorage = async (file: File, index: number, userId: string): Promise<string | null> => {
    const ext = file.name.split('.').pop();
    const unique = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2);
    const path = `${userId}/aprovacao/${pageSlug}/${Date.now()}-${index}-${unique}.${ext}`;
    const { error } = await supabase.storage.from('client-approval-images').upload(path, file);
    if (error) return null;
    const { data } = supabase.storage.from('client-approval-images').getPublicUrl(path);
    return data.publicUrl;
  };

  const handleFiles = async (files: FileList | File[]) => {
    const imageFiles = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (!imageFiles.length) return;
    setUploading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setUploading(false); return; }
    const optimized = await optimizeImages(imageFiles);
    setOptStats(optimized);
    const urls = await Promise.all(optimized.map((r, i) => uploadToStorage(r.file, i, user.id)));
    const validUrls = urls.filter((u): u is string => Boolean(u));
    if (validUrls.length) {
      await supabase.from('client_page_images').insert(
        validUrls.map((url, i) => ({ page_id: pageId, image_url: url, sort_order: images.length + i, layout_type: 'single' })),
      );
      await fetchImages();
    }
    setUploading(false);
    setTimeout(() => setOptStats([]), 8000);
  };

  const removeImage = async (id: string) => {
    await supabase.from('client_page_images').delete().eq('id', id);
    fetchImages();
  };

  const removeImageSide = async (imgId: string, side: 'left' | 'right') => {
    const img = images.find(i => i.id === imgId);
    if (!img) return;
    if (img.layout_type === 'pair') {
      if (side === 'right') {
        await supabase.from('client_page_images').update({ layout_type: 'single', pair_image_url: null }).eq('id', imgId);
      } else {
        await supabase.from('client_page_images').update({ layout_type: 'single', image_url: img.pair_image_url!, pair_image_url: null }).eq('id', imgId);
      }
      fetchImages();
    } else {
      removeImage(imgId);
    }
  };

  const saveCaption = async (id: string, value: string) => {
    await supabase.from('client_page_images').update({ caption: value || null }).eq('id', id);
    fetchImages();
  };

  const handleRowDragStart = (e: React.DragEvent, index: number) => {
    setDragRowIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleRowDrop = async (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (dragRowIndex === null || dragRowIndex === targetIndex) { setDragRowIndex(null); return; }
    const reordered = [...images];
    const [moved] = reordered.splice(dragRowIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    setImages(reordered);
    setDragRowIndex(null);
    await Promise.all(reordered.map((img, i) => supabase.from('client_page_images').update({ sort_order: i }).eq('id', img.id)));
  };

  const swapSides = async (imgId: string) => {
    const img = images.find(i => i.id === imgId);
    if (!img || img.layout_type !== 'pair' || !img.pair_image_url) return;
    await supabase.from('client_page_images').update({ image_url: img.pair_image_url, pair_image_url: img.image_url }).eq('id', imgId);
    fetchImages();
  };

  if (loading) return <p style={{ fontFamily: monoFont, fontSize: 12, color: 'rgba(247,246,242,0.4)' }}>Carregando imagens...</p>;

  return (
    <div className="mt-4">
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Remover imagem"
        description={deleteTarget?.label || 'Deseja remover esta imagem?'}
        confirmLabel="Remover"
        cancelLabel="Cancelar"
        destructive
        onConfirm={() => { if (deleteTarget) removeImage(deleteTarget.id); }}
      />

      <GalleryUploadZone uploading={uploading} onFiles={handleFiles} />

      {optStats.length > 0 && optStats.some(s => s.savedPercent > 0) && (
        <div className="p-3 mb-4 flex flex-col gap-1" style={{ border: '0.5px solid hsl(120 40% 50% / 0.2)', backgroundColor: 'hsl(120 40% 50% / 0.05)' }}>
          <div className="flex items-center gap-2 mb-1">
            <Lightning size={12} weight="thin" style={{ color: 'hsl(120 40% 40%)' }} />
            <span className="text-[10px] uppercase tracking-wider" style={{ fontFamily: monoFont, color: 'hsl(120 40% 40%)' }}>
              Otimização concluída
            </span>
          </div>
          {optStats.filter(s => s.savedPercent > 0).map((stat, i) => (
            <span key={i} className="text-[10px]" style={{ fontFamily: monoFont, opacity: 0.7 }}>
              {formatOptimizationStats(stat)}
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-1">
        {images.map((img, i) => {
          const isPair = img.layout_type === 'pair' && Boolean(img.pair_image_url);

          const rowData: GalleryRowData = {
            id: img.id,
            leftUrl: img.image_url,
            rightUrl: img.pair_image_url || '',
            title: img.caption,
            sortOrder: img.sort_order,
            approveCount: img.approve_count,
            rejectCount: img.reject_count,
          };

          return (
            <GalleryRowCard
              key={img.id}
              row={rowData}
              index={i}
              mode="approval"
              isDragging={dragRowIndex === i}
              onDragStart={(e) => handleRowDragStart(e, i)}
              onDragEnd={() => setDragRowIndex(null)}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
              onDrop={(e) => handleRowDrop(e, i)}
              renderLeftSlot={
                <div className="overflow-hidden" style={{ width: 64, height: 64 }}>
                  <img src={img.image_url} alt="" className="w-full h-full object-cover" />
                </div>
              }
              renderRightSlot={
                isPair && img.pair_image_url ? (
                  <div className="overflow-hidden" style={{ width: 64, height: 64 }}>
                    <img src={img.pair_image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                ) : undefined
              }
              onSwapSides={isPair ? () => swapSides(img.id) : undefined}
              onSaveTitle={(val) => saveCaption(img.id, val)}
              onDeleteRow={() => setDeleteTarget({ id: img.id, label: 'Deseja remover esta linha e suas imagens?' })}
              onDeleteLeft={isPair ? () => removeImageSide(img.id, 'left') : () => setDeleteTarget({ id: img.id, label: 'Deseja remover esta imagem?' })}
              onDeleteRight={isPair ? () => removeImageSide(img.id, 'right') : undefined}
            />
          );
        })}
      </div>

      {images.length === 0 && (
        <p className="text-xs text-center py-8" style={{ fontFamily: monoFont, color: 'rgba(247,246,242,0.4)' }}>
          Nenhuma imagem ainda. Arraste fotos para a área acima.
        </p>
      )}
    </div>
  );
}
